/**
 * Is a workspace git-tracked, and how — the PURE half of gitSyncStatus.js,
 * split out (MCP audit card MCP 8, 04.10.2026) so the MCP server can report
 * tracking without importing the sync service, the git status store or the
 * offline queue (the MCP perimeter test walks every import, dynamic ones
 * included, and `gitSyncStatus.js` reaches the git commands).
 *
 * No imports: nothing here can reach a Tauri command or a store.
 */

/**
 * Workspaces whose mirror is a git repository Apiboo keeps — synced to a
 * remote OR tracked only on this device (Track on this device, T1). The Git
 * page's list, the status bar and the request History button go by this.
 * Git-storage folders are the user's own repositories and are not.
 */
export function isGitTracked(workspace) {
  return Boolean(workspace?.git) && workspace?.storage !== 'git';
}

/** Tracked in a local repository with no remote (`git.provider === 'local'`). */
export function isLocalOnly(workspace) {
  return isGitTracked(workspace) && workspace.git.provider === 'local';
}

/**
 * Workspaces that sync to a REMOTE through the mirror: the offline queue,
 * Pull, the status fetch and "Open on …" go by this. Local links are not.
 */
export function isRemoteLinked(workspace) {
  return isGitTracked(workspace) && workspace.git.provider !== 'local';
}

/**
 * Whether a tracked workspace has something not yet saved (local history) or
 * not yet sent (remote link), from its git status store entry — the same
 * reading describeSync's "waiting" tone makes. null when nothing is known yet
 * (status never checked): "no changes" would be a claim nobody verified.
 */
export function changesWaitingOf(entry, { local = false } = {}) {
  const st = entry?.status;
  if (!local && entry?.queued) return true;
  if (!st) return null;
  if (local) return Boolean(st.hasLocalChanges);
  return Boolean(st.hasLocalChanges) || (st.ahead || 0) > 0;
}

// The app registers how to read the status store (gitSyncStatus.js does, on
// load); this module never imports the store. The reader answers ONE boolean
// (or null) per workspace — no status object, no error text, no URL.
let changesWaitingReader = null;

/** Called once by gitSyncStatus.js: `(workspace) => boolean | null`. */
export function setChangesWaitingReader(fn) {
  changesWaitingReader = typeof fn === 'function' ? fn : null;
}

/** true / false, or null when unknown (no reader registered, never checked). */
export function changesWaitingFor(workspace) {
  if (!isGitTracked(workspace) || !changesWaitingReader) return null;
  try {
    const v = changesWaitingReader(workspace);
    return typeof v === 'boolean' ? v : null;
  } catch {
    return null;
  }
}

// The git providers an answer may name; anything else is "other" (a future or
// hand-edited link never puts its own string on the wire).
const GIT_PROVIDERS = ['github', 'gitlab', 'gitea', 'remote'];

/**
 * Git tracking, READ ONLY (MCP audit card MCP 8, 04.10.2026): so an agent can
 * tell the user "this workspace is tracked and your change is waiting to be
 * saved / synced". Four facts and nothing else — NEVER the remote URL, owner,
 * repository, branch, account, Vault item or a sync error text (pinned by
 * mcpWorkspaceGit.test.js). Save version / Sync are deliberately NOT over MCP:
 * the user does that in the Git page.
 */
export function gitTrackingReport(workspace) {
  if (!isGitTracked(workspace)) return { tracked: false };
  const local = isLocalOnly(workspace);
  const provider = local ? 'local' : workspace.git.provider || 'github';
  return {
    tracked: true,
    kind: local ? 'local' : 'remote',
    provider: local || GIT_PROVIDERS.includes(provider) ? provider : 'other',
    changesWaiting: changesWaitingFor(workspace),
  };
}
